import {
    applicationRepository,
    eventRepository,
    playerRepository,
    seasonRepository,
    teamRepository
} from "../../storage/storage";
import {
    addDays,
    isoDateComparer,
    mergeCollectionsById
} from "../common/object-util";
import { mapEvent } from "../../object-mappers/event-mapper";
import { mapTeam } from "../../object-mappers/team-mapper";
import mapSeason from "../../object-mappers/season-mapper";
import { endOfDay, startOfDay } from "date-fns";
import TeamPlayer from "../../sports/common/players/TeamPlayer";
import NoPlayer from "../../sports/common/players/NoPlayer";

class BaseDataStrategy {
    async getEventByExternalId(eventId) {
        return eventRepository.where("eventId").equals(eventId).first();
    }

    async getPlayers(externalTeamId, includeDeleted = false) {
        const team = await teamRepository
            .where("teamId")
            .equals(externalTeamId)
            .first();
        return playerRepository
            .where("teamId")
            .equals(team.id)
            .filter((p) => includeDeleted || !p.deleted)
            .toArray();
    }

    async getTeam(externalTeamId) {
        return teamRepository.where("teamId").equals(externalTeamId).first();
    }

    async getSeasons(sportCodes = []) {
        const seasons = await seasonRepository.getAll().toArray();
        return sportCodes?.length
            ? seasons.filter((season) =>
                  sportCodes.some((id) => season.sport.sportId === id)
              )
            : seasons;
    }

    async getSeasonTeams(seasonId, externalTeamIds) {
        const seasonTeams = await teamRepository.where({ seasonId }).toArray();

        return seasonTeams.filter((st) => externalTeamIds.includes(st.teamId));
    }

    async getSeason(externalSeasonId) {
        return seasonRepository.where({ seasonId: externalSeasonId }).first();
    }

    eventsFilter(e, season, startDate, endDate, status) {
        const { seasonId, startDateTime, statusCode } = e;
        const eventTime = new Date(startDateTime).getTime();

        return (
            seasonId === season.seasonId &&
            (!startDate || eventTime >= startDate.getTime()) &&
            (!endDate || eventTime <= endDate.getTime()) &&
            (!status || statusCode === status)
        );
    }

    async getEventsByCriteria(localSeasonId, filterOptions, pagingOptions) {
        const { pageSize, pageNumber } = {
            pageSize: 999999,
            pageNumber: 0,
            ...pagingOptions
        };
        const { statusCode } = filterOptions || {};
        const startDate = null;
        const endDate = null;
        const season = await seasonRepository.getById(localSeasonId);
        const events = await eventRepository
            .getAll()
            .offset(pageNumber * pageSize)
            .limit(pageSize)
            .filter((e) =>
                this.eventsFilter(e, season, startDate, endDate, statusCode)
            )
            .toArray();

        const totalCount = await eventRepository
            .getAll()
            .filter((e) =>
                this.eventsFilter(e, season, startDate, endDate, statusCode)
            )
            .count();

        return {
            data: events,
            totalElements: totalCount,
            totalPages: Math.ceil(totalCount / (1 + pageSize)),
            pageSize: pageSize,
            pageNumber: pageNumber
        };
    }

    async getLastWeekEvents(sportCodes = []) {
        const now = new Date();
        const startDate = addDays(now, -7);

        return eventRepository
            .getAll()
            .filter((e) => {
                const { startDateTime, sportId } = e;
                const eventTime = new Date(startDateTime).getTime();

                return (
                    eventTime >= startDate &&
                    eventTime <= now &&
                    sportCodes.includes(sportId)
                );
            })
            .toArray();
    }

    async getNextWeekEvents(sportCodes = []) {
        const now = new Date();
        const endDate = addDays(now, 7);

        return eventRepository
            .getAll()
            .filter((e) => {
                const { startDateTime, sportId } = e;
                const eventTime = new Date(startDateTime).getTime();

                return (
                    eventTime >= now &&
                    eventTime <= endDate &&
                    sportCodes.includes(sportId)
                );
            })
            .toArray();
    }

    async saveOrUpdatePlayer(player) {
        const id = await playerRepository.put(player);
        return await playerRepository.getById(id);
    }

    async updateCurrentEvent(mappedEvent) {
        let currentEvent = await eventRepository
            .where("eventId")
            .equals(mappedEvent.eventId)
            .first();

        if (currentEvent) {
            Object.assign(currentEvent, mappedEvent);
        } else {
            currentEvent = mappedEvent;
        }
        return eventRepository.put(currentEvent);
    }

    async saveOrUpdateEvent(jsonEvent) {
        if (!jsonEvent) {
            return null;
        }
        const { teams } = jsonEvent;
        const { homeTeam, awayTeam } = teams;

        const [savedHomeTeam, savedAwayTeam] = await Promise.all([
            this.saveOrUpdateTeam(homeTeam),
            this.saveOrUpdateTeam(awayTeam)
        ]);

        const mappedEvent = Object.assign(mapEvent(jsonEvent), {
            teams: {
                homeTeam: savedHomeTeam,
                awayTeam: savedAwayTeam
            }
        });

        const id = await this.updateCurrentEvent(mappedEvent);

        return eventRepository.getById(id);
    }

    async saveOrUpdatePlayers(teamId, remotePlayers = []) {
        if (remotePlayers.length) {
            const localPlayers = await playerRepository
                .where("teamId")
                .equals(teamId)
                .toArray();

            const mergedPlayers = mergeCollectionsById(
                localPlayers,
                remotePlayers,
                "playerId"
            );
            const teamPlayer = mergedPlayers.find(
                (p) => p.uniform === TeamPlayer.getUniform()
            );
            if (!teamPlayer) {
                mergedPlayers.push(new TeamPlayer(teamId));
            }

            const nonePlayer = mergedPlayers.find(
                (p) => p.uniform === NoPlayer.getUniform()
            );
            if (!nonePlayer) {
                mergedPlayers.push(new NoPlayer(teamId));
            }

            const specialUniforms = [
                TeamPlayer.getUniform(),
                NoPlayer.getUniform()
            ];
            const remoteIds = new Set(remotePlayers.map((p) => p.playerId));
            mergedPlayers.forEach((player) => {
                // Never logically delete special players or locally-added
                // players that have not been synced to the API yet.
                if (
                    specialUniforms.includes(player.uniform) ||
                    !player.playerId
                ) {
                    return;
                }
                if (remoteIds.has(player.playerId)) {
                    player.restore();
                } else {
                    player.markDeleted();
                }
            });

            return playerRepository.bulkPut(mergedPlayers);
        }

        return [];
    }

    async getOrCreateSeason(jsonSeason) {
        let season = await seasonRepository.getBySeasonId(jsonSeason.seasonId);
        if (!season) {
            const seasonId = await seasonRepository.put(mapSeason(jsonSeason));
            season = await seasonRepository.getById(seasonId);
        }
        return season;
    }

    async getOrCreateTeam(jsonTeam) {
        let team = await teamRepository
            .where("teamId")
            .equals(jsonTeam.teamId)
            .first();
        if (!team) {
            const teamId = await teamRepository.put(mapTeam(jsonTeam));
            team = await teamRepository.getById(teamId);
        }

        return team;
    }

    async saveOrUpdateTeam(jsonTeam) {
        let team = await teamRepository
            .where("teamId")
            .equals(jsonTeam.teamId)
            .first();

        team = mapTeam(jsonTeam, team);
        team.id = await teamRepository.put(team);

        return team;
    }

    async getTimeZones() {
        const appConfig = await applicationRepository.getAll().first();
        return appConfig?.timeZones || null;
    }

    async getEventsByDateSportCodeAndStatusCode(date, sportCode, statusCodes) {
        const events = await eventRepository
            .where({ sportId: sportCode })
            .filter((e) => {
                const { startDateTime, statusCode } = e;
                const eventTime = new Date(startDateTime).getTime();

                return (
                    eventTime >= startOfDay(date) &&
                    eventTime <= endOfDay(date) &&
                    statusCodes.includes(statusCode)
                );
            })
            .toArray();

        return events.sort((a, b) =>
            isoDateComparer(a.startDateTime, b.startDateTime)
        );
    }

    async saveEventData() {}

    async saveBoxscore() {}

    async syncTeamPlayers() {}
}

export default BaseDataStrategy;
